import {
  DataStatusNote,
  formatClassSection,
  formatDate,
  formatDuration,
  formatPercent,
  formatScore,
  GIL,
  GIL_CELL,
  GilColumn,
  GilGallery,
  GilReportTable,
  gilSize,
  gilText,
  initialsOf,
  LearningOutcomesCard,
  OverviewSection,
  parseIdParam,
  performanceLabel,
  projectLabel,
  ReportLayout,
  ReportTabs,
  SectionCard,
  StatTile,
  StatusChip,
  toPercent,
  uploadsToMedia,
  useCommonServiceClientContext,
  useGilReport,
} from '@geneo2-web/shared-ui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Typography } from '@mui/material';
import { GilProjectStatusEnum } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { GilGroupMemberRow } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GIL_Reports_Student_Project } from '../../../routeHandling/RoutesNomenclature';

type GroupTab = 'members' | 'evidence';

const TABS = [
  { key: 'members', label: 'Group Members', icon: <PeopleOutlineIcon /> },
  { key: 'evidence', label: 'Evidence', icon: <ImageOutlinedIcon /> },
];

/** Round slate monogram beside each member's name. */
const avatarSx = {
  ...gilText(14, 600, '#FFFFFF'),
  width: gilSize(40, 34),
  height: gilSize(40, 34),
  borderRadius: '50%',
  backgroundColor: '#5F6B85',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
};

/** Dashboard 3 - one group's members and work on one project. */
export const GilGroupReport = () => {
  const navigate = useNavigate();
  const { assessment_id, student_group_id } = useParams();
  const assessmentId = parseIdParam(assessment_id);
  const studentGroupId = parseIdParam(student_group_id);
  const { LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [tab, setTab] = useState<GroupTab>('members');

  const { state, reload } = useGilReport(
    () =>
      assessmentId && studentGroupId
        ? LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getGilTeacherProjectGroupReport(
            { assessmentId, studentGroupId }
          ).then((response) => response.data)
        : undefined,
    [assessmentId, studentGroupId]
  );

  const report = state.status === 'success' ? state.data : undefined;
  const members = useMemo(() => report?.members ?? [], [report?.members]);
  const groupLabel = report?.groupName || 'Whole class';
  const classLabel = `Class ${formatClassSection(report?.classSection)}`;
  const studentCount = report?.studentCount ?? members.length;
  //~ A collaborative group submits one set of evidence together. Without groups
  //~ every student submits their own, so it is shown against each student.
  const isGroupProject = !!report?.groupName;
  const groupEvidence = useMemo(
    () => (isGroupProject ? uploadsToMedia(report?.userUploads) : []),
    [isGroupProject, report?.userUploads]
  );
  const evidenceOf = (member: GilGroupMemberRow) =>
    uploadsToMedia(
      (report?.userUploads ?? []).filter(
        (upload) => upload.personId.toString() === member.studentId.toString()
      )
    );

  /** Figures the summary shows, derived from the members' own rows. */
  const stats = useMemo(() => {
    const submitted = members.filter(
      (member) =>
        member.status === GilProjectStatusEnum.GIL_PROJECT_STATUS_COMPLETED
    ).length;
    //~ Same rule as the project report: members who never opened the project
    //~ don't pull the average time down.
    const timed = members.filter(
      (member) => (member.timeSpent?.totalTimeInSec ?? 0) > 0
    );
    const avgTime = timed.length
      ? timed.reduce(
          (sum, member) => sum + (member.timeSpent?.totalTimeInSec ?? 0),
          0
        ) / timed.length
      : 0;
    const graded = members.filter((member) => member.projectScore?.isGraded);
    const avgScore = graded.length
      ? graded.reduce(
          (sum, member) =>
            sum +
            toPercent(
              member.projectScore?.obtainedMarks,
              member.projectScore?.maxMarks
            ),
          0
        ) / graded.length
      : undefined;
    return { submitted, avgTime, avgScore };
  }, [members]);

  const openStudent = (member: GilGroupMemberRow) =>
    navigate(
      `${GIL_Reports_Student_Project}/${member.studentId.toString()}/${assessmentId}`
    );

  const indexColumn: GilColumn<GilGroupMemberRow> = {
    key: 'index',
    header: '#',
    render: (_member, index) => (
      <Typography sx={GIL_CELL.index}>{index + 1}</Typography>
    ),
  };
  //~ The underlined name is the drill-down into the student's project report.
  const studentColumn: GilColumn<GilGroupMemberRow> = {
    key: 'name',
    header: 'Student',
    minWidth: 220,
    render: (member) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: gilSize(16, 10) }}>
        <Box sx={avatarSx} aria-hidden>
          {initialsOf(member.studentName)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            role="link"
            tabIndex={0}
            onClick={() => openStudent(member)}
            onKeyDown={(event) => event.key === 'Enter' && openStudent(member)}
            sx={GIL_CELL.link}
          >
            {member.studentName}
          </Typography>
          <Typography noWrap sx={GIL_CELL.sub}>
            {member.rollNumber ? `Roll No: ${member.rollNumber}` : classLabel}
          </Typography>
        </Box>
      </Box>
    ),
  };
  const scoreColumn: GilColumn<GilGroupMemberRow> = {
    key: 'score',
    header: 'Project Score',
    render: (member) => formatScore(member.projectScore),
  };
  const statusColumn: GilColumn<GilGroupMemberRow> = {
    key: 'status',
    header: 'Status',
    render: (member) => <StatusChip status={member.status} />,
  };

  //~ A collaboration group shares one lab device and login, so per-member time
  //~ and coding only show whoever was logged in. For a group the figures are
  //~ the group's own; per-student columns stay for students working alone.
  const timeColumns: GilColumn<GilGroupMemberRow>[] = [
    {
      key: 'total',
      header: 'Total Time',
      render: (member) => formatDuration(member.timeSpent?.totalTimeInSec),
    },
    {
      key: 'video',
      header: 'Resources',
      render: (member) =>
        formatDuration(member.timeSpent?.videoActivityBookTimeInSec),
    },
    {
      key: 'coding',
      header: 'Coding',
      render: (member) => formatDuration(member.timeSpent?.codingTimeInSec),
    },
  ];

  const memberColumns: GilColumn<GilGroupMemberRow>[] = [
    indexColumn,
    studentColumn,
    ...(isGroupProject ? [] : timeColumns),
    scoreColumn,
    statusColumn,
  ];

  return (
    <ReportLayout
      showImage
      title={
        report
          ? `${projectLabel(report.project)} | ${groupLabel}`
          : 'Group Report'
      }
      subtitle={
        report
          ? [classLabel, report.teacherName].filter(Boolean).join(' | ')
          : undefined
      }
      meta={
        report && [
          {
            label: 'Group',
            value: groupLabel,
            color: '#FFF8E6',
            textColor: GIL.muted,
          },
          {
            label: 'Students',
            value: studentCount,
            iconName: 'user',
            color: '#EEF0FF',
            textColor: '#2E3192',
          },
        ]
      }
      state={state}
      onRetry={reload}
    >
      <DataStatusNote status={report?.dataStatus} />

      <OverviewSection subtitle="Quick summary of group activity and performance">
        <StatTile
          tone="green"
          icon={<PersonOutlineIcon />}
          value={studentCount}
          label={studentCount === 1 ? 'Student' : 'Students'}
        />
        <StatTile
          tone="blue"
          icon="gil-submitted-blue"
          value={`${stats.submitted}/${members.length}`}
          label="Submitted"
        />
        {isGroupProject ? (
          <StatTile
            tone="yellow"
            icon={<AccessTimeIcon />}
            value={formatDuration(report?.groupTimeSpent?.totalTimeInSec)}
            label="Group Time"
            caption={
              report?.lastActivityAt
                ? `Last worked ${formatDate(report.lastActivityAt)}`
                : undefined
            }
          />
        ) : (
          <StatTile
            tone="yellow"
            icon={<AccessTimeIcon />}
            value={formatDuration(stats.avgTime)}
            label="Avg. Time"
            caption="Per Student"
          />
        )}
        <StatTile
          tone="red"
          icon="gil-star-red"
          value={formatPercent(stats.avgScore)}
          label="Project Score"
          caption={performanceLabel(stats.avgScore)}
        />
      </OverviewSection>

      <ReportTabs
        tabs={TABS}
        value={tab}
        onChange={(key) => setTab(key as GroupTab)}
      />

      {tab === 'members' && (
        <>
          <SectionCard
            plain
            subtitle="Detailed performance of each student in this group"
          >
            <GilReportTable
              columns={memberColumns}
              rows={members}
              rowKey={(member) => member.studentId.toString()}
              emptyText="No students in this group"
            />
          </SectionCard>
          <LearningOutcomesCard
            outcomes={report?.learningOutcomes}
            subtitle={`How ${groupLabel} is doing on each learning outcome of this project`}
          />
        </>
      )}

      {tab === 'evidence' &&
        (isGroupProject ? (
          <SectionCard plain subtitle={`Evidence uploaded by ${groupLabel}`}>
            <GilGallery items={groupEvidence} title={groupLabel} />
          </SectionCard>
        ) : (
          members.map((member) => (
            <SectionCard
              key={member.studentId.toString()}
              plain
              subtitle={`Evidence uploaded by ${member.studentName}`}
            >
              <GilGallery
                items={evidenceOf(member)}
                title={member.studentName}
              />
            </SectionCard>
          ))
        ))}
    </ReportLayout>
  );
};

export default GilGroupReport;
